import { Sfx } from './audio.js';
import { FEEL } from './feel.js';

const canvas = document.getElementById('game');
const ctx = canvas.getContext('2d', { alpha: false });
const overlay = document.getElementById('overlay');
const finalScoreEl = document.getElementById('final-score');
const bestLineEl = document.getElementById('best-line');
const retryBtn = document.getElementById('retry');
const hintEl = document.getElementById('hint');

const WUXING = [
  { id: 'jin',  char: '金', fill: '#e8eef5', glow: '#c0d0e8', rim: '#9aa8bc' },
  { id: 'mu',   char: '木', fill: '#2ecc71', glow: '#57e092', rim: '#1a8f4a' },
  { id: 'shui', char: '水', fill: '#2a6fd6', glow: '#5aa0ff', rim: '#1a3f8a' },
  { id: 'huo',  char: '火', fill: '#e74c3c', glow: '#ff6b4a', rim: '#9b1c12' },
  { id: 'tu',   char: '土', fill: '#c9a227', glow: '#e0c060', rim: '#8a6b14' },
];

const BEST_KEY = 'wuxing-ring-best';

/** @type {ReturnType<typeof freshState>} */
let G;
let W = 390, H = 844, DPR = 1;
let CX = 195, CY = 400, R = 160;
let lastT = 0;
let shakeT = 0;
let particles = [];
let burns = [];
let floats = [];
let stars = [];
let mountains = [];
let mistPhase = 0;
let ready = false;

/** 贴图：水墨底 + 金朱笔触环（public/art）；未就绪时走程序化回退 */
const art = { bg: null, ring: null };
const RING_TEX_OUTER_FRAC = 0.9375; // 笔触外缘约占半幅（1024 图 ~480/512）

function loadArtImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

const artLoadPromise = Promise.all([
  loadArtImage('./art/bg-ink.jpg'),
  loadArtImage('./art/ring-brush.png'),
]).then(([bg, ring]) => {
  art.bg = bg;
  art.ring = ring;
  return art;
});

const keys = { up: false, down: false, left: false, right: false };

function freshState() {
  return {
    phase: 'playing', // playing | dead
    px: 0,
    py: 0,
    aim: -Math.PI / 2, // shoot/face angle
    score: 0,
    streak: 0,
    bullets: [],   // {x,y,vx,vy,hostile,life,bounces}
    orbs: [],      // {angle, radius, kind, pulse, hp}
    fireCd: 0,
    aliveMs: 0,
    spawnCd: 400,
    deathAt: 0,
  };
}

function resize() {
  DPR = Math.min(window.devicePixelRatio || 1, 2.5);
  W = window.innerWidth;
  H = window.innerHeight;
  canvas.width = Math.floor(W * DPR);
  canvas.height = Math.floor(H * DPR);
  canvas.style.width = W + 'px';
  canvas.style.height = H + 'px';
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  CX = W * 0.5;
  CY = H * 0.46;
  R = Math.min(W, H) * 0.45; // 贴近 D-ink：环外缘近左右边距
  buildDecor();
  if (G && G.phase) clampPlayer(true);
}

function buildDecor() {
  stars = [];
  for (let i = 0; i < 90; i++) {
    stars.push({
      x: Math.random() * W,
      y: Math.random() * H * 0.72,
      r: 0.4 + Math.random() * 1.4,
      a: 0.15 + Math.random() * 0.55,
      tw: Math.random() * Math.PI * 2,
    });
  }
  mountains = [];
  mountains.push(makeRidge(H * 0.55, H * 0.22, 0.18, 7));
  mountains.push(makeRidge(H * 0.68, H * 0.28, 0.32, 5));
  mountains.push(makeRidge(H * 0.82, H * 0.26, 0.55, 4));
}

function makeRidge(baseY, amp, alpha, peaks) {
  const pts = [];
  const n = 28;
  for (let i = 0; i <= n; i++) {
    const x = (i / n) * W;
    let y = baseY;
    for (let k = 1; k <= peaks; k++) {
      y -= Math.sin(i * 0.4 + k * 1.7) * (amp / k) * (0.5 + 0.5 * Math.sin(k * 2.1));
    }
    pts.push({ x, y });
  }
  return { pts, alpha, baseY };
}

function playerMaxR() {
  const ringT = FEEL.ringThickness * (R / 160);
  const pr = FEEL.playerSize * (R / 160) * 0.7;
  return Math.max(8, R - ringT * 0.5 - pr);
}

/** Keep player inside outer ring; soft clamp at inner face. */
function clampPlayer(hard = false) {
  const dx = G.px - CX, dy = G.py - CY;
  const d = Math.hypot(dx, dy) || 0;
  const maxD = playerMaxR();
  if (d > maxD) {
    const nx = dx / (d || 1), ny = dy / (d || 1);
    G.px = CX + nx * maxD;
    G.py = CY + ny * maxD;
    if (!hard) {
      // light outward cancel — stay clamped
    }
  }
}

function kindScale(kind, scale) {
  if (scale != null) return scale;
  if (kind === 4) return FEEL.orbScaleTu; // 土略大
  return 1;
}

function kindHp(kind) {
  if (kind === 0) return FEEL.jinHp;   // 金
  if (kind === 2) return FEEL.shuiHp;  // 水
  if (kind === 4) return FEEL.tuHp;    // 土
  return 1;
}

/** Orbit angular speed factor by element (土慢、火快). */
function kindAng(kind) {
  if (kind === 4) return 0.045; // 土
  if (kind === 3) return 0.22;  // 火
  if (kind === 1) return 0.10;  // 木
  if (kind === 2) return 0.08;  // 水
  return 0.12;                  // 金
}

function orbHitR(o) {
  return FEEL.orbRadius * (R / 160) * (o.scale || 1);
}

function orbDrawR(o) {
  return orbHitR(o) * (1 + 0.04 * Math.sin(o.pulse));
}

function makeOrb({ kind, angle, radius, scale } = {}) {
  const k = kind == null ? ((Math.random() * WUXING.length) | 0) : kind;
  const sc = kindScale(k, scale);
  return {
    angle: angle ?? Math.random() * Math.PI * 2,
    radius: radius ?? R * 0.5,
    kind: k,
    pulse: Math.random() * Math.PI * 2,
    hitFlash: 0,
    hp: kindHp(k),
    scale: sc,
    drift: 0,
    angMul: kindAng(k),
  };
}

function randOrb(kind) {
  const k = kind == null ? ((Math.random() * WUXING.length) | 0) : kind;
  const or = orbHitR({ scale: kindScale(k), pulse: 0 });
  const minR = or + R * 0.12;
  const prPad = FEEL.playerSize * (R / 160) + or + 18;
  let best = null;
  for (let attempt = 0; attempt < 24; attempt++) {
    const a = Math.random() * Math.PI * 2;
    const pr = Math.max(minR, R * (0.34 + Math.random() * 0.4));
    const ox = CX + Math.cos(a) * pr;
    const oy = CY + Math.sin(a) * pr;
    const away = (!G || G.px == null) ? 1e9 : Math.hypot(ox - G.px, oy - G.py);
    if (away >= prPad) return makeOrb({ kind: k, angle: a, radius: pr });
    if (!best || away > best.away) best = { away, angle: a, radius: pr };
  }
  return makeOrb({ kind: k, angle: best.angle, radius: best.radius });
}

function metalBurst(x, y, color) {
  for (let i = 0; i < 22; i++) {
    const a = Math.random() * Math.PI * 2;
    const s = 60 + Math.random() * 180;
    particles.push({
      x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
      life: 0.4 + Math.random() * 0.35, t: 0, color, r: 1 + Math.random() * 2.2,
      shard: true,
    });
  }
}

function scorePop(ox, oy, wx, mul = 1) {
  G.streak += 1;
  const pts = Math.round((FEEL.scoreBase + (G.streak - 1) * FEEL.streakBonus) * mul);
  G.score += pts;
  addFloat(ox, oy - 10, `+${pts}`, wx.fill);
  return pts;
}

function smashOrbAt(i, ox, oy, opts = {}) {
  const o = G.orbs[i];
  if (!o) return;
  const wx = WUXING[o.kind];
  const mul = opts.mul ?? 1;
  scorePop(ox, oy, wx, mul);
  if (opts.metal) metalBurst(ox, oy, wx.glow);
  else burst(ox, oy, wx.glow, opts.burstN ?? 18);
  Sfx.play('pop', { k: 0.9 + Math.random() * 0.2 });
  G.orbs.splice(i, 1);
}

function spawnBurn(x, y) {
  burns.push({
    x, y,
    r: FEEL.burnRadius * (R / 160),
    life: FEEL.burnMs / 1000,
    max: FEEL.burnMs / 1000,
  });
}

/** 火连锁：邻近球扣 1 血，扣穿则碎 */
function fireSplash(x, y, skipIdx) {
  const br = FEEL.burnRadius * (R / 160);
  for (let j = G.orbs.length - 1; j >= 0; j--) {
    if (j === skipIdx) continue;
    const o2 = G.orbs[j];
    const ox2 = CX + Math.cos(o2.angle) * o2.radius;
    const oy2 = CY + Math.sin(o2.angle) * o2.radius;
    if (Math.hypot(ox2 - x, oy2 - y) > br + orbHitR(o2)) continue;
    o2.hp -= 1;
    o2.hitFlash = 0.4;
    if (o2.hp <= 0) {
      smashOrbAt(j, ox2, oy2, { mul: 0.6, burstN: 12 });
    }
  }
}

function splitMu(o, ox, oy) {
  const wx = WUXING[o.kind];
  scorePop(ox, oy, wx, 1);
  burst(ox, oy, wx.glow, 14);
  Sfx.play('pop', { k: 1.05 });
  const kids = [];
  for (const sign of [-1, 1]) {
    kids.push(makeOrb({
      kind: 1,
      angle: o.angle + sign * 0.32,
      radius: Math.min(playerMaxR() * 0.92, o.radius + sign * 6),
      scale: FEEL.orbScaleChild,
    }));
  }
  return kids;
}

function reset() {
  G = freshState();
  // start lower-mid inside ring, aim toward center
  G.px = CX;
  G.py = CY + R * 0.42;
  clampPlayer(true);
  G.aim = Math.atan2(CY - G.py, CX - G.px);
  G.orbs = [];
  for (let i = 0; i < 4; i++) G.orbs.push(randOrb());
  particles = [];
  burns = [];
  floats = [];
  overlay.classList.remove('show');
  hintEl.style.opacity = '0.7';
  setTimeout(() => { if (G.phase === 'playing') hintEl.style.opacity = '0'; }, 3200);
}

function playerPos() {
  return { x: G.px, y: G.py, aim: G.aim };
}

function shoot() {
  if (G.phase !== 'playing') return false;
  if (G.fireCd > 0) return false;
  G.fireCd = FEEL.shootCooldownMs / 1000;
  const sp = FEEL.bulletSpeed * (R / 160);
  const c = Math.cos(G.aim), s = Math.sin(G.aim);
  G.bullets.push({
    x: G.px + c * 12,
    y: G.py + s * 12,
    vx: c * sp,
    vy: s * sp,
    hostile: false,
    life: 4,
    bounces: 0,
  });
  Sfx.play('shoot', { k: 1 });
  return true;
}

function kill() {
  if (G.phase !== 'playing') return;
  G.phase = 'dead';
  G.deathAt = performance.now();
  Sfx.play('fail');
  shakeT = FEEL.hitShakeMs / 1000;
  const best = Math.max(G.score, Number(localStorage.getItem(BEST_KEY) || 0));
  localStorage.setItem(BEST_KEY, String(best));
  finalScoreEl.textContent = `得分 ${G.score}`;
  bestLineEl.textContent = best > G.score ? `最高 ${best}` : (G.score > 0 ? '新纪录' : '');
  setTimeout(() => overlay.classList.add('show'), FEEL.deathFreezeMs);
}

function addFloat(x, y, text, color) {
  floats.push({ x, y, text, color, t: 0, life: FEEL.floatScoreMs / 1000 });
}

function burst(x, y, color, n = 14) {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2;
    const s = 40 + Math.random() * 140;
    particles.push({
      x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s,
      life: 0.35 + Math.random() * 0.35, t: 0, color, r: 1.5 + Math.random() * 2.5,
    });
  }
}

/** Reflect velocity about radial normal; count bounce; fade after max. */
function bounceBullet(b, nx, ny) {
  const dot = b.vx * nx + b.vy * ny;
  b.vx -= 2 * dot * nx;
  b.vy -= 2 * dot * ny;
  b.bounces = (b.bounces || 0) + 1;
  if (b.bounces >= FEEL.maxBounces) {
    b.life = Math.min(b.life, FEEL.bounceFadeLife);
  }
}

function movePlayer(dt) {
  let mx = 0, my = 0;
  if (keys.left) mx -= 1;
  if (keys.right) mx += 1;
  if (keys.up) my -= 1;
  if (keys.down) my += 1;

  // virtual stick (mobile) — continuous vector, overrides digital keys when active
  if (stick.active && (Math.abs(stick.x) > 0.05 || Math.abs(stick.y) > 0.05)) {
    mx = stick.x;
    my = stick.y;
  }

  // canvas drag-follow only when touch pad is NOT shown (desktop / fine pointer)
  if (!useTouchPad && dragging && pointerWorld) {
    const tx = pointerWorld.x;
    const ty = pointerWorld.y;
    const dx = tx - G.px, dy = ty - G.py;
    const dist = Math.hypot(dx, dy);
    if (dist > 1.5) {
      const sp = FEEL.playerSpeed * (R / 160) * 1.35;
      const step = Math.min(dist, sp * dt);
      G.px += (dx / dist) * step;
      G.py += (dy / dist) * step;
      G.aim = Math.atan2(dy, dx);
      mx = 0; my = 0;
    }
  }

  if (mx !== 0 || my !== 0) {
    const len = Math.hypot(mx, my) || 1;
    const sp = FEEL.playerSpeed * (R / 160);
    G.px += (mx / len) * sp * dt;
    G.py += (my / len) * sp * dt;
    G.aim = Math.atan2(my / len, mx / len);
  }

  clampPlayer(false);

  // hold fire button → auto-fire on cooldown
  if (fireHeld) shoot();
}

function update(dt) {
  mistPhase += dt;
  if (shakeT > 0) shakeT = Math.max(0, shakeT - dt);

  for (const p of particles) { p.t += dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 0.96; p.vy *= 0.96; }
  particles = particles.filter((p) => p.t < p.life);
  for (const f of floats) { f.t += dt; f.y -= 36 * dt; }
  floats = floats.filter((f) => f.t < f.life);

  if (G.phase !== 'playing') return;

  movePlayer(dt);

  G.aliveMs += dt * 1000;
  G.fireCd = Math.max(0, G.fireCd - dt);
  G.spawnCd -= dt * 1000;
  if (G.spawnCd <= 0 && G.orbs.length < FEEL.maxOrbs) {
    G.orbs.push(randOrb());
    G.spawnCd = FEEL.spawnIntervalMs * (0.75 + Math.random() * 0.5);
  }

  for (const o of G.orbs) {
    const dir = o.kind % 2 === 0 ? 1 : -1;
    o.angle += dt * (o.angMul ?? kindAng(o.kind)) * dir;
    o.pulse += dt * 2.4;
    if (o.hitFlash > 0) o.hitFlash -= dt;
    if (o.drift) {
      o.radius = Math.min(playerMaxR() * 0.95, o.radius + o.drift * dt);
      o.drift *= Math.max(0, 1 - dt * 0.7);
      if (o.drift < 2) o.drift = 0;
    }
  }

  for (const bn of burns) bn.life -= dt;
  burns = burns.filter((bn) => bn.life > 0);

  // bullets — outer ring elastic bounce only (no center disc); max FEEL.maxBounces
  const ringT = FEEL.ringThickness * (R / 160);
  const ringInner = R - ringT * 0.5;
  const bulletR = 5;
  for (const b of G.bullets) {
    b.x += b.vx * dt;
    b.y += b.vy * dt;
    b.life -= dt;
    const dx = b.x - CX, dy = b.y - CY;
    const dist = Math.hypot(dx, dy) || 1;
    const nx = dx / dist, ny = dy / dist;
    const vDotN = b.vx * nx + b.vy * ny;

    // outer ring inner face: elastic reflect when moving outward
    if (dist >= ringInner - bulletR && vDotN > 0) {
      bounceBullet(b, nx, ny);
      const push = ringInner - bulletR - 1;
      b.x = CX + nx * push;
      b.y = CY + ny * push;
      // unmatched / rebound becomes hostile (反弹打己)
      if (!b.hostile) {
        b.hostile = true;
        const sp = FEEL.hostileSpeed * (R / 160);
        const spd = Math.hypot(b.vx, b.vy) || 1;
        b.vx = (b.vx / spd) * sp;
        b.vy = (b.vy / spd) * sp;
      }
      Sfx.play('reject');
      burst(b.x, b.y, '#d4af37', 6);
    }

    if (dist > R + 60) b.life = 0;
  }

  // bullet vs orbs — 五行差异；友好/敌弹/反弹弹均可作用
  for (const b of G.bullets) {
    if (b.life <= 0) continue;
    for (let i = G.orbs.length - 1; i >= 0; i--) {
      const o = G.orbs[i];
      const ox = CX + Math.cos(o.angle) * o.radius;
      const oy = CY + Math.sin(o.angle) * o.radius;
      const or = orbHitR(o);
      if (Math.hypot(b.x - ox, b.y - oy) >= or + 6) continue;

      const designSpd = Math.hypot(b.vx, b.vy) / (R / 160);
      const wx = WUXING[o.kind];

      // 金：硬；普通弹两下，快弹或已反弹弹一击碎
      if (o.kind === 0) {
        const hard = designSpd >= FEEL.jinFastOneShot || (b.bounces || 0) > 0;
        o.hp -= hard ? o.hp : 1;
        o.hitFlash = 0.35;
        b.life = 0;
        if (o.hp <= 0) smashOrbAt(i, ox, oy, { metal: true, burstN: 20 });
        else { burst(ox, oy, wx.glow, 6); Sfx.play('reject'); }
        break;
      }

      // 木：中弹分裂成 2 个更小木球（子球再中则碎）
      if (o.kind === 1) {
        b.life = 0;
        if ((o.scale || 1) > FEEL.orbScaleChild * 1.15) {
          const kids = splitMu(o, ox, oy);
          G.orbs.splice(i, 1);
          for (const k of kids) G.orbs.push(k);
        } else {
          smashOrbAt(i, ox, oy, { burstN: 12 });
        }
        break;
      }

      // 水：折射，子弹偏转后继续飞；球慢漂移；两下碎
      if (o.kind === 2) {
        const sp = Math.hypot(b.vx, b.vy) || 1;
        const ang = Math.atan2(b.vy, b.vx) + (Math.random() < 0.5 ? -1 : 1) * FEEL.shuiDeflect;
        b.vx = Math.cos(ang) * sp;
        b.vy = Math.sin(ang) * sp;
        // 推出球外避免粘连
        const nx = (b.x - ox) / (Math.hypot(b.x - ox, b.y - oy) || 1);
        const ny = (b.y - oy) / (Math.hypot(b.x - ox, b.y - oy) || 1);
        b.x = ox + nx * (or + 8);
        b.y = oy + ny * (or + 8);
        o.hp -= 1;
        o.hitFlash = 0.3;
        o.drift = FEEL.shuiDrift * (R / 160);
        if (o.hp <= 0) {
          smashOrbAt(i, ox, oy, { burstN: 16 });
          b.life = 0;
        } else {
          burst(ox, oy, wx.glow, 5);
          Sfx.play('reject');
        }
        break;
      }

      // 火：高分碎 + 短灼烧连锁扣邻近血
      if (o.kind === 3) {
        b.life = 0;
        spawnBurn(ox, oy);
        smashOrbAt(i, ox, oy, { mul: FEEL.huoScoreMul, burstN: 22 });
        fireSplash(ox, oy, -1);
        break;
      }

      // 土：挡弹一次再碎；略大已由 scale
      if (o.kind === 4) {
        b.life = 0;
        o.hp -= 1;
        o.hitFlash = 0.35;
        if (o.hp <= 0) smashOrbAt(i, ox, oy, { burstN: 16 });
        else { burst(ox, oy, wx.glow, 7); Sfx.play('reject'); }
        break;
      }

      // fallback
      b.life = 0;
      smashOrbAt(i, ox, oy);
      break;
    }
  }

  G.bullets = G.bullets.filter((b) => b.life > 0);

  const p = playerPos();
  const pr = FEEL.playerSize * 0.55;
  for (const b of G.bullets) {
    if (!b.hostile) continue;
    if (Math.hypot(b.x - p.x, b.y - p.y) < pr + 5) {
      kill();
      return;
    }
  }
  for (const o of G.orbs) {
    const ox = CX + Math.cos(o.angle) * o.radius;
    const oy = CY + Math.sin(o.angle) * o.radius;
    const or = orbHitR(o);
    if (Math.hypot(ox - p.x, oy - p.y) < or + pr) {
      kill();
      return;
    }
  }
}

/* ---------------- draw ---------------- */
function drawBg() {
  if (art.bg && art.bg.naturalWidth) {
    const iw = art.bg.naturalWidth;
    const ih = art.bg.naturalHeight;
    const scale = Math.max(W / iw, H / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    const dx = (W - dw) * 0.5;
    const dy = (H - dh) * 0.5;
    ctx.drawImage(art.bg, dx, dy, dw, dh);
    // 极淡暗罩，保分数/球可读；不叠程序化山/月（避免双月亮/脏叠）
    ctx.fillStyle = 'rgba(4,6,12,0.20)';
    ctx.fillRect(0, 0, W, H);
    return;
  }

  // 贴图未就绪时的短回退（避免黑屏）
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, '#0b0e16');
  g.addColorStop(0.45, '#12151f');
  g.addColorStop(1, '#08090e');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  for (const m of mountains) {
    ctx.beginPath();
    ctx.moveTo(0, H);
    for (const p of m.pts) ctx.lineTo(p.x, p.y);
    ctx.lineTo(W, H);
    ctx.closePath();
    ctx.fillStyle = `rgba(8,10,14,${m.alpha})`;
    ctx.fill();
  }
}

function drawRing() {
  const t = FEEL.ringThickness * (R / 160);
  if (art.ring && art.ring.naturalWidth) {
    // 外缘对齐碰撞外径 R；几何仍用现有 R / ringThickness，仅换皮
    const size = (2 * R) / RING_TEX_OUTER_FRAC;
    ctx.save();
    ctx.translate(CX, CY);
    ctx.shadowColor = 'rgba(180,60,30,0.35)';
    ctx.shadowBlur = 22;
    ctx.drawImage(art.ring, -size * 0.5, -size * 0.5, size, size);
    ctx.restore();
    // 极淡内沿提示（碰撞中线附近），不挡水墨透底
    ctx.beginPath();
    ctx.arc(CX, CY, R - t * 0.5, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(255,210,140,0.07)';
    ctx.lineWidth = 1.25;
    ctx.stroke();
    return;
  }

  // 回退：旧程序化金朱环（无中心暗盘，避免挡住贴图透底）
  ctx.save();
  ctx.shadowColor = 'rgba(200,60,40,0.45)';
  ctx.shadowBlur = 28;
  ctx.beginPath();
  ctx.arc(CX, CY, R, 0, Math.PI * 2);
  ctx.arc(CX, CY, R - t, 0, Math.PI * 2, true);
  const rg = ctx.createLinearGradient(CX - R, CY - R, CX + R, CY + R);
  rg.addColorStop(0, '#c9a227');
  rg.addColorStop(0.35, '#8b1e18');
  rg.addColorStop(0.65, '#d4af37');
  rg.addColorStop(1, '#5a120e');
  ctx.fillStyle = rg;
  ctx.fill();
  ctx.restore();
}

function drawScore() {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `600 ${Math.floor(R * 0.42)}px "Songti SC","Noto Serif SC",serif`;
  ctx.fillStyle = 'rgba(255,230,180,0.12)';
  ctx.fillText(String(G.score), CX, CY);
  if (G.streak >= 2) {
    ctx.font = `500 ${Math.floor(R * 0.09)}px "Songti SC","Noto Serif SC",serif`;
    ctx.fillStyle = 'rgba(220,60,50,0.75)';
    ctx.fillText(`逆转 · ${G.streak}`, CX, CY + R * 0.22);
  }
  ctx.restore();
}

function drawOrb(o) {
  const wx = WUXING[o.kind];
  const ox = CX + Math.cos(o.angle) * o.radius;
  const oy = CY + Math.sin(o.angle) * o.radius;
  const t = o.pulse;
  const kind = o.kind;
  // 绘制半径；木呼吸略强（仅视觉，碰撞仍用 orbHitR）
  let or = kind === 1
    ? orbHitR(o) * (1 + 0.09 * Math.sin(t))
    : orbDrawR(o);
  // 土：慢沉浮视觉偏移
  const dx = 0;
  const dy = kind === 4 ? Math.sin(t * 0.55) * or * 0.08 : 0;
  const x = ox + dx;
  const y = oy + dy;

  ctx.save();

  // —— idle 底层（球后）——
  if (kind === 2) {
    // 水：涟漪扩散
    for (let i = 0; i < 3; i++) {
      const phase = (t * 0.65 + i * 0.9) % 2.5;
      const rr = or * (1.12 + phase * 0.5);
      const a = Math.max(0, 0.38 * (1 - phase / 2.5));
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(150,210,255,${a})`;
      ctx.lineWidth = 1.4;
      ctx.stroke();
    }
  }
  if (kind === 3) {
    // 火：外焰抖动（三角瓣）
    for (let i = 0; i < 7; i++) {
      const a0 = (i / 7) * Math.PI * 2 + t * 2.1;
      const wob = 1 + 0.2 * Math.sin(t * 5.5 + i * 1.3);
      const fr = or * (1.12 + 0.28 * wob);
      ctx.beginPath();
      ctx.moveTo(x + Math.cos(a0) * or * 0.35, y + Math.sin(a0) * or * 0.35);
      ctx.lineTo(
        x + Math.cos(a0 - 0.32) * or * 0.95,
        y + Math.sin(a0 - 0.32) * or * 0.95,
      );
      ctx.lineTo(x + Math.cos(a0) * fr, y + Math.sin(a0) * fr);
      ctx.lineTo(
        x + Math.cos(a0 + 0.32) * or * 0.95,
        y + Math.sin(a0 + 0.32) * or * 0.95,
      );
      ctx.closePath();
      ctx.fillStyle = i % 2
        ? `rgba(255,150,40,${0.32})`
        : `rgba(230,50,20,${0.26})`;
      ctx.fill();
    }
  }

  ctx.shadowColor = wx.glow;
  ctx.shadowBlur = kind === 3 ? 22 : 18;
  const g = ctx.createRadialGradient(x - or * 0.3, y - or * 0.3, or * 0.1, x, y, or);
  g.addColorStop(0, '#fff');
  g.addColorStop(0.25, wx.fill);
  g.addColorStop(1, wx.rim);
  ctx.fillStyle = g;
  ctx.beginPath(); ctx.arc(x, y, or, 0, Math.PI * 2); ctx.fill();
  ctx.shadowBlur = 0;

  // 金：金属高光缓慢旋转扫过
  if (kind === 0) {
    const ang = t * 1.15;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, or * 0.96, 0, Math.PI * 2);
    ctx.clip();
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const hx = x + ca * or * 0.2;
    const hy = y + sa * or * 0.2;
    const px = -sa;
    const py = ca;
    const hg = ctx.createLinearGradient(
      hx - px * or, hy - py * or,
      hx + px * or, hy + py * or,
    );
    hg.addColorStop(0, 'rgba(255,255,255,0)');
    hg.addColorStop(0.42, 'rgba(255,255,255,0)');
    hg.addColorStop(0.5, 'rgba(255,255,255,0.62)');
    hg.addColorStop(0.58, 'rgba(255,255,255,0)');
    hg.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hg;
    ctx.fillRect(x - or, y - or, or * 2, or * 2);
    ctx.restore();
  }

  // 水：冰蓝高光扫过
  if (kind === 2) {
    const ang = t * 0.9 + 1.2;
    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, or * 0.96, 0, Math.PI * 2);
    ctx.clip();
    const ca = Math.cos(ang);
    const sa = Math.sin(ang);
    const hg = ctx.createLinearGradient(
      x - sa * or, y + ca * or,
      x + sa * or, y - ca * or,
    );
    hg.addColorStop(0, 'rgba(180,230,255,0)');
    hg.addColorStop(0.48, 'rgba(180,230,255,0)');
    hg.addColorStop(0.5, 'rgba(210,240,255,0.5)');
    hg.addColorStop(0.52, 'rgba(180,230,255,0)');
    hg.addColorStop(1, 'rgba(180,230,255,0)');
    ctx.fillStyle = hg;
    ctx.fillRect(x - or, y - or, or * 2, or * 2);
    ctx.restore();
  }

  // 金/土未碎完时淡环提示剩余血
  if (o.hp > 1) {
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(x, y, or + 3, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.fillStyle = 'rgba(10,12,18,0.78)';
  ctx.font = `700 ${Math.floor(or * 1.05)}px "Songti SC","Noto Serif SC",serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(wx.char, x, y + 1);
  if (o.hitFlash > 0) {
    ctx.fillStyle = `rgba(255,255,255,${Math.min(0.85, o.hitFlash)})`;
    ctx.beginPath(); ctx.arc(x, y, or, 0, Math.PI * 2); ctx.fill();
  }

  // —— idle 顶层（球前）——
  if (kind === 1) {
    // 木：叶脉短线 + 绿点轻飘
    ctx.strokeStyle = 'rgba(40,120,70,0.45)';
    ctx.lineWidth = 1;
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + t * 0.15;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + Math.cos(a) * or * 0.78, y + Math.sin(a) * or * 0.78);
      ctx.stroke();
    }
    for (let i = 0; i < 4; i++) {
      const life = (t * 0.45 + i * 0.41) % 1;
      const a = t * 0.6 + i * 1.6;
      const px = x + Math.cos(a) * or * (0.4 + 0.5 * life);
      const py = y + Math.sin(a) * or * 0.35 - life * or * 0.55;
      ctx.fillStyle = `rgba(80,220,120,${0.55 * (1 - life)})`;
      ctx.beginPath(); ctx.arc(px, py, 1.3, 0, Math.PI * 2); ctx.fill();
    }
  }
  if (kind === 2) {
    // 水/冰：边缘六角碎晶闪烁
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2 + t * 0.25;
      const blink = 0.3 + 0.7 * Math.max(0, Math.sin(t * 3.2 + i * 1.05));
      const px = x + Math.cos(a) * or * 0.78;
      const py = y + Math.sin(a) * or * 0.78;
      const rr = or * 0.11;
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const aa = a + (k / 6) * Math.PI * 2;
        const sx = px + Math.cos(aa) * rr;
        const sy = py + Math.sin(aa) * rr;
        if (k === 0) ctx.moveTo(sx, sy); else ctx.lineTo(sx, sy);
      }
      ctx.closePath();
      ctx.fillStyle = `rgba(200,235,255,${0.55 * blink})`;
      ctx.fill();
    }
  }
  if (kind === 3) {
    // 火：橙红粒子上飘
    for (let i = 0; i < 5; i++) {
      const life = (t * 0.95 + i * 0.37) % 1;
      const px = x + Math.sin(t * 2.2 + i * 2.1) * or * 0.4;
      const py = y - or * 0.25 - life * or * 1.5;
      const a = 1 - life;
      ctx.fillStyle = `rgba(255,${110 + i * 18},35,${0.75 * a})`;
      ctx.beginPath();
      ctx.arc(px, py, 1.1 + a * 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  if (kind === 4) {
    // 土：尘粒环绕
    for (let i = 0; i < 5; i++) {
      const a = t * 0.35 + i * 1.35;
      const px = x + Math.cos(a) * or * (0.95 + 0.12 * Math.sin(t + i));
      const py = y + Math.sin(a * 0.8) * or * 0.55 + or * 0.25;
      ctx.fillStyle = `rgba(170,130,55,${0.3 + 0.25 * Math.sin(t * 1.2 + i)})`;
      ctx.beginPath(); ctx.arc(px, py, 1.15 + (i % 2) * 0.4, 0, Math.PI * 2); ctx.fill();
    }
  }

  ctx.restore();
}

function drawBurns() {
  for (const bn of burns) {
    const a = Math.max(0, bn.life / bn.max);
    ctx.save();
    const g = ctx.createRadialGradient(bn.x, bn.y, 2, bn.x, bn.y, bn.r);
    g.addColorStop(0, `rgba(255,120,40,${0.45 * a})`);
    g.addColorStop(0.55, `rgba(230,60,30,${0.22 * a})`);
    g.addColorStop(1, 'rgba(230,60,30,0)');
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(bn.x, bn.y, bn.r, 0, Math.PI * 2); ctx.fill();
    ctx.restore();
  }
}

function drawPlayer() {
  const p = playerPos();
  const ang = G.aim;
  const s = FEEL.playerSize * (R / 160);
  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(ang);
  ctx.shadowColor = '#ffd76a';
  ctx.shadowBlur = 16;
  ctx.beginPath();
  ctx.moveTo(s * 1.1, 0);
  ctx.lineTo(-s * 0.7, s * 0.65);
  ctx.lineTo(-s * 0.35, 0);
  ctx.lineTo(-s * 0.7, -s * 0.65);
  ctx.closePath();
  const pg = ctx.createLinearGradient(-s, 0, s, 0);
  pg.addColorStop(0, '#8b5a00');
  pg.addColorStop(0.5, '#ffd76a');
  pg.addColorStop(1, '#fff3c0');
  ctx.fillStyle = pg;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = 'rgba(255,240,180,0.7)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.globalAlpha = 0.35;
  ctx.beginPath();
  ctx.moveTo(-s * 0.9, 0);
  ctx.lineTo(-s * 2.2, s * 0.25);
  ctx.lineTo(-s * 2.2, -s * 0.25);
  ctx.closePath();
  ctx.fillStyle = '#ffc84a';
  ctx.fill();
  ctx.restore();
}

function drawBullets() {
  for (const b of G.bullets) {
    ctx.save();
    if (b.hostile) {
      ctx.shadowColor = '#ff5533';
      ctx.shadowBlur = 12;
      ctx.fillStyle = '#ff6644';
    } else {
      ctx.shadowColor = '#ffe08a';
      ctx.shadowBlur = 10;
      ctx.fillStyle = '#ffe9a8';
    }
    ctx.beginPath();
    ctx.arc(b.x, b.y, b.hostile ? 5.5 : 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function drawParticles() {
  for (const p of particles) {
    const a = 1 - p.t / p.life;
    ctx.fillStyle = p.color;
    ctx.globalAlpha = a;
    if (p.shard) {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.atan2(p.vy, p.vx));
      ctx.fillRect(-p.r * 1.8, -p.r * 0.35, p.r * 3.6 * a, p.r * 0.7);
      ctx.restore();
    } else {
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * a, 0, Math.PI * 2); ctx.fill();
    }
  }
  ctx.globalAlpha = 1;
}

function drawFloats() {
  for (const f of floats) {
    const a = 1 - f.t / f.life;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = f.color;
    ctx.font = `700 ${Math.floor(18 + 6 * a)}px "Songti SC","Noto Serif SC",serif`;
    ctx.textAlign = 'center';
    ctx.fillText(f.text, f.x, f.y);
    ctx.restore();
  }
}

function drawTitle() {
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(240,210,140,0.55)';
  ctx.font = `500 ${Math.floor(14)}px "Songti SC","Noto Serif SC",serif`;
  ctx.fillText('五行环', CX, Math.max(28, CY - R - 36));
  ctx.restore();
}

function frame(now) {
  const dt = Math.min(0.033, (now - lastT) / 1000 || 0.016);
  lastT = now;
  update(dt);

  let ox = 0, oy = 0;
  if (shakeT > 0) {
    const k = shakeT / (FEEL.hitShakeMs / 1000);
    ox = (Math.random() - 0.5) * FEEL.hitShakePx * k * 2;
    oy = (Math.random() - 0.5) * FEEL.hitShakePx * k * 2;
  }
  ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  ctx.clearRect(0, 0, W, H);
  ctx.save();
  ctx.translate(ox, oy);
  drawBg();
  drawRing();
  drawScore();
  for (const o of G.orbs) drawOrb(o);
  drawBurns();
  drawBullets();
  drawPlayer();
  drawParticles();
  drawFloats();
  drawTitle();
  ctx.restore();

  requestAnimationFrame(frame);
}

/* ---------------- input ---------------- */
let dragging = false;
let pointerId = null;
/** @type {{x:number,y:number}|null} */
let pointerWorld = null;

/** Mobile virtual pad: left stick + fire. When shown, canvas drag does not move the plane. */
const stick = { active: false, x: 0, y: 0, pointerId: null };
let fireHeld = false;
let useTouchPad = false;

const touchControlsEl = document.getElementById('touch-controls');
const stickEl = document.getElementById('stick');
const stickKnob = document.getElementById('stick-knob');
const btnFire = document.getElementById('btn-fire');

function preferTouchPad() {
  try {
    if (window.matchMedia('(pointer: coarse)').matches) return true;
    if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) return true;
  } catch {}
  return ('ontouchstart' in window) && Math.min(window.innerWidth, window.innerHeight) <= 900;
}

function syncTouchPadUi() {
  useTouchPad = preferTouchPad();
  document.body.classList.toggle('has-touch-pad', useTouchPad);
  if (touchControlsEl) {
    touchControlsEl.classList.toggle('show', useTouchPad);
    touchControlsEl.setAttribute('aria-hidden', useTouchPad ? 'false' : 'true');
  }
  if (hintEl) {
    hintEl.textContent = useTouchPad
      ? '左摇杆移动 · 右键射击 · 轻点画布也可射'
      : '环内拖移 · 点击射击 · WASD';
  }
}

function worldAt(clientX, clientY) {
  const rect = canvas.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}

function setStickFromEvent(clientX, clientY) {
  if (!stickEl) return;
  const rect = stickEl.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const maxR = rect.width * 0.38;
  let dx = clientX - cx;
  let dy = clientY - cy;
  const d = Math.hypot(dx, dy) || 1;
  if (d > maxR) { dx = (dx / d) * maxR; dy = (dy / d) * maxR; }
  stick.x = dx / maxR;
  stick.y = dy / maxR;
  if (stickKnob) stickKnob.style.transform = `translate(${dx}px, ${dy}px)`;
}

function resetStick() {
  stick.active = false;
  stick.x = 0;
  stick.y = 0;
  stick.pointerId = null;
  if (stickKnob) stickKnob.style.transform = 'translate(0, 0)';
}

let canvasPointerDown = false;

function onDown(e) {
  Sfx.unlock();
  if (G.phase !== 'playing') return;
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  // with touch pad: canvas only records tap-to-shoot, never drag-follow
  canvasPointerDown = true;
  dragging = !useTouchPad;
  pointerId = ev.pointerId ?? 't';
  pointerWorld = useTouchPad ? null : worldAt(ev.clientX, ev.clientY);
  canvas._downAt = performance.now();
  canvas._downX = ev.clientX;
  canvas._downY = ev.clientY;
  e.preventDefault?.();
}

function onMove(e) {
  if (!canvasPointerDown || useTouchPad || G.phase !== 'playing') return;
  if (!dragging) return;
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  pointerWorld = worldAt(ev.clientX, ev.clientY);
  e.preventDefault?.();
}

function onUp(e) {
  if (!canvasPointerDown) return; // ignore stick/fire pointerups
  const ev = e.changedTouches ? e.changedTouches[0] : e;
  const dt = performance.now() - (canvas._downAt || 0);
  const dx = ev.clientX - (canvas._downX || 0);
  const dy = ev.clientY - (canvas._downY || 0);
  canvasPointerDown = false;
  dragging = false;
  pointerWorld = null;
  // light tap on canvas shoots (mobile: pad on; desktop: short click)
  if (dt < 280 && Math.hypot(dx, dy) < 14) shoot();
  e.preventDefault?.();
}

canvas.addEventListener('pointerdown', onDown, { passive: false });
canvas.addEventListener('pointermove', onMove, { passive: false });
window.addEventListener('pointerup', onUp, { passive: false });
canvas.addEventListener('touchstart', onDown, { passive: false });
canvas.addEventListener('touchmove', onMove, { passive: false });
canvas.addEventListener('touchend', onUp, { passive: false });

if (stickEl) {
  const stickDown = (e) => {
    Sfx.unlock();
    e.preventDefault();
    e.stopPropagation();
    const ev = e.changedTouches ? e.changedTouches[0] : e;
    stick.active = true;
    stick.pointerId = ev.pointerId ?? 'stick';
    setStickFromEvent(ev.clientX, ev.clientY);
  };
  const stickMove = (e) => {
    if (!stick.active) return;
    e.preventDefault();
    const ev = e.changedTouches ? e.changedTouches[0] : e;
    if (stick.pointerId != null && ev.pointerId != null && stick.pointerId !== ev.pointerId) return;
    setStickFromEvent(ev.clientX, ev.clientY);
  };
  const stickUp = (e) => {
    if (!stick.active) return;
    e.preventDefault();
    resetStick();
  };
  stickEl.addEventListener('pointerdown', stickDown, { passive: false });
  window.addEventListener('pointermove', stickMove, { passive: false });
  window.addEventListener('pointerup', stickUp, { passive: false });
  window.addEventListener('pointercancel', stickUp, { passive: false });
  stickEl.addEventListener('touchstart', stickDown, { passive: false });
  stickEl.addEventListener('touchmove', stickMove, { passive: false });
  stickEl.addEventListener('touchend', stickUp, { passive: false });
  stickEl.addEventListener('touchcancel', stickUp, { passive: false });
}

if (btnFire) {
  let firePointerId = null;
  const fireDown = (e) => {
    Sfx.unlock();
    e.preventDefault();
    e.stopPropagation();
    const ev = e.changedTouches ? e.changedTouches[0] : e;
    firePointerId = ev.pointerId ?? 'fire';
    fireHeld = true;
    btnFire.classList.add('held');
    shoot();
  };
  const fireUp = (e) => {
    if (!fireHeld) return;
    const ev = e.changedTouches ? e.changedTouches[0] : e;
    if (firePointerId != null && ev && ev.pointerId != null && firePointerId !== ev.pointerId) return;
    e?.preventDefault?.();
    fireHeld = false;
    firePointerId = null;
    btnFire.classList.remove('held');
  };
  btnFire.addEventListener('pointerdown', fireDown, { passive: false });
  window.addEventListener('pointerup', fireUp, { passive: false });
  window.addEventListener('pointercancel', fireUp, { passive: false });
  btnFire.addEventListener('touchstart', fireDown, { passive: false });
  btnFire.addEventListener('touchend', fireUp, { passive: false });
  btnFire.addEventListener('touchcancel', fireUp, { passive: false });
  btnFire.addEventListener('click', (e) => { e.preventDefault(); });
}

function setKey(code, down) {
  if (code === 'ArrowLeft' || code === 'KeyA') keys.left = down;
  if (code === 'ArrowRight' || code === 'KeyD') keys.right = down;
  if (code === 'ArrowUp' || code === 'KeyW') keys.up = down;
  if (code === 'ArrowDown' || code === 'KeyS') keys.down = down;
}

window.addEventListener('keydown', (e) => {
  Sfx.unlock();
  if (e.code === 'Space' || e.code === 'KeyJ') { e.preventDefault(); shoot(); }
  setKey(e.code, true);
  if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'KeyA', 'KeyD', 'KeyW', 'KeyS'].includes(e.code)) {
    e.preventDefault();
  }
});
window.addEventListener('keyup', (e) => setKey(e.code, false));

retryBtn.addEventListener('click', () => { Sfx.unlock(); reset(); });

window.addEventListener('resize', () => { resize(); syncTouchPadUi(); });
syncTouchPadUi();

/* ---------------- boot + smoke API ---------------- */
resize();
reset();
requestAnimationFrame((t) => { lastT = t; requestAnimationFrame(frame); });

function markReady() {
  if (ready) return;
  ready = true;
  window.__WUXING_RING_READY__ = true;
}

// 尽量等贴图就绪再亮 READY（冒烟截图要见水墨底+笔触环）；超时仍放行
const artWait = Promise.race([
  artLoadPromise,
  new Promise((r) => setTimeout(r, 2000)),
]);
artWait.finally(markReady);
window.__WUXING_RING__ = {
  get phase() { return G.phase; },
  get score() { return G.score; },
  get streak() { return G.streak; },
  get angle() { return G.aim; },
  setAngle(a) { G.aim = a; },
  get orbCount() { return G.orbs.length; },
  get bulletCount() { return G.bullets.length; },
  get hostileCount() { return G.bullets.filter((b) => b.hostile).length; },
  get maxBouncesOnField() {
    return G.bullets.reduce((m, b) => Math.max(m, b.bounces || 0), 0);
  },
  get layout() {
    return {
      W, H, CX, CY, R, dpr: DPR,
      centerDiscR: 0,
      ringInner: R - FEEL.ringThickness * (R / 160) * 0.5,
      playerMaxR: playerMaxR(),
    };
  },
  playerScreen() {
    const p = playerPos();
    return { x: p.x, y: p.y, angle: G.aim, aim: G.aim };
  },
  setPlayer(x, y) {
    G.px = x; G.py = y; clampPlayer(true); return playerPos();
  },
  orbsScreen() {
    return G.orbs.map((o) => ({
      x: CX + Math.cos(o.angle) * o.radius,
      y: CY + Math.sin(o.angle) * o.radius,
      kind: o.kind,
      char: WUXING[o.kind].char,
      radius: orbHitR(o),
      hp: o.hp,
      scale: o.scale || 1,
    }));
  },
  shoot,
  reset,
  kill,
  /** Aim player toward a given orb index (from free position). */
  aimAtOrb(i = 0) {
    const o = G.orbs[i];
    if (!o) return false;
    const ox = CX + Math.cos(o.angle) * o.radius;
    const oy = CY + Math.sin(o.angle) * o.radius;
    G.aim = Math.atan2(oy - G.py, ox - G.px);
    return true;
  },
  wipeOrbs() { G.orbs.length = 0; return true; },
  /** Test helper: inject a bullet (hostile/bounced can smash orbs). */
  injectBullet({ x, y, vx, vy, hostile = false, bounces = 0, life = 4 } = {}) {
    G.bullets.push({ x, y, vx: vx ?? 0, vy: vy ?? 0, hostile: !!hostile, bounces: bounces | 0, life });
    return G.bullets.length;
  },
  get touchPad() {
    return {
      useTouchPad,
      shown: !!(touchControlsEl && touchControlsEl.classList.contains('show')),
      stick: { x: stick.x, y: stick.y, active: stick.active },
      fireHeld,
    };
  },
  forceTouchPad(on) {
    useTouchPad = !!on;
    document.body.classList.toggle('has-touch-pad', useTouchPad);
    if (touchControlsEl) {
      touchControlsEl.classList.toggle('show', useTouchPad);
      touchControlsEl.setAttribute('aria-hidden', useTouchPad ? 'false' : 'true');
    }
    return this.touchPad;
  },
  ensureFiveElements() {
    const have = new Set(G.orbs.map((o) => o.kind));
    for (let k = 0; k < 5; k++) {
      if (!have.has(k)) {
        G.orbs.push(makeOrb({
          kind: k,
          angle: -Math.PI / 2 + (k / 5) * Math.PI * 2,
          radius: R * 0.55,
        }));
      }
    }
    return G.orbs.map((o) => WUXING[o.kind].char);
  },
  /** Aim at first orb matching 五行 char, e.g. '火'. */
  aimAtChar(ch) {
    const i = G.orbs.findIndex((o) => WUXING[o.kind].char === ch);
    if (i < 0) return false;
    return this.aimAtOrb(i);
  },
  samplePixels() {
    const w = canvas.width, h = canvas.height;
    const data = ctx.getImageData(0, 0, w, h).data;
    const at = (x, y) => {
      const i = (Math.floor(y * DPR) * w + Math.floor(x * DPR)) * 4;
      return [data[i], data[i + 1], data[i + 2], data[i + 3]];
    };
    // 月亮：多点取最亮（宽屏 cover 会裁掉原稿左上月，单点易抖）
    const moonCands = [
      at(W * 0.18, H * 0.12),
      at(W * 0.22, H * 0.10),
      at(W * 0.12, H * 0.08),
      at(W * 0.28, H * 0.14),
      at(W * 0.35, H * 0.10),
      at(W * 0.08, H * 0.16),
    ];
    const moon = moonCands.reduce((a, b) =>
      (a[0] + a[1] + a[2] >= b[0] + b[1] + b[2] ? a : b));
    return {
      center: at(CX, CY),
      ring: at(CX + R - 4, CY),
      moon,
      player: (() => { const p = playerPos(); return at(p.x, p.y); })(),
    };
  },
};
